import React from "react";
import {useTranslation} from "react-i18next";
import {validEmptyText} from "../../../utils/textOptions";
import {IconButton, Spinner} from "@material-tailwind/react";

/**
 * Componente `CustomIconButton`:
 *
 * Botón de ícono personalizado con soporte para `Tooltip`, estados de carga (`loading`) y des habilitación.
 * Se utiliza para representar acciones con íconos, incluyendo spinner cuando se está procesando una acción.
 *
 * **Props**:
 * @param loading (boolean, opcional): Si es `true`, se muestra un spinner en lugar del contenido del botón.
 * @param onClick (function, opcional): Función que se ejecuta al hacer clic en el botón.
 * @param disabled (boolean, opcional): Si es `true`, el botón estará deshabilitado.
 * @param className (string, opcional): Clases CSS adicionales para el botón.
 * @param classNameTooltip (string, opcional): Clases CSS adicionales para el tooltip.
 * @param classNameLoading (string, opcional): Clases CSS para el spinner mostrado durante el loading.
 * @param tooltip (string, opcional): Texto que se muestra en el tooltip al pasar el cursor sobre el botón.
 * @param children (React.ReactNode, opcional): Contenido del botón (generalmente un ícono).
 *
 * **Características**:
 * - Muestra un tooltip con el contenido definido.
 * - Si `loading` es verdadero, se reemplaza el contenido con un spinner (`CustomSpinner`).
 * - Totalmente estilizado con Tailwind CSS y Material Tailwind.
 * - Puede usarse como botón de acción rápida con íconos (editar, eliminar, etc.).
 */
interface Props {
    loading?: boolean,
    type?: 'button' | 'tooltip',
    onClick?: () => void,
    disabled?: boolean,
    className?: string,
    classNameTooltip?: string,
    classNameLoading?: string,
    tooltip?: string,
    label?: string,
    children?: React.ReactNode
}
export default function CustomIconButton({...props}: Props): JSX.Element {
    const {t} = useTranslation("global") as { t: (key: string) => string };

    return (
        <>

                {props.type === 'tooltip' ? (
                    <span className={props.className}>{props.children}</span>
                ) : props.label ? (
                        <div
                            tabIndex={-1}
                            className={'flex flex-row items-center w-[200px]'}
                        >
                            <IconContainerButton {...props}/>
                            <span className={'text-orange-47 leading-3 -mt-[3px]'}>{t(props.label)}</span>
                        </div>
                    ) :
                    (<IconContainerButton {...props}/>)
                }

        </>
    )
}

const IconContainerButton = React.forwardRef<HTMLButtonElement, Props>((props, ref) => {
    return (
        <IconButton
            ref={ref}
            placeholder=""
            tabIndex={-1}
            autoFocus={false}
            className={`grid items-center rounded-[10px] min-h-[35px] h-[35px] w-[35px] sm:min-h-[25px] sm:h-[25px] sm:w-[25px] disabled:opacity-60 ${validEmptyText(props.className)}`}
            disabled={!!props.loading || !!props.disabled}
            onClick={props.onClick}
        >
            {!!props.loading
                ? <Spinner />
                : props.children ?? <></>
            }
        </IconButton>
    );
});
